/* eslint-disable regex/invalid */
import { ButtonOwnerState, extendTheme } from '@mui/joy';
import { CaretDown } from '@phosphor-icons/react';

import { customColors } from './constants';
import { Color, Level, Orientation, Size, TooltipPosition, Variant } from './enums';
import {
  styleOverridesAlert,
  styleOverridesAutocomplete,
  styleOverridesAutocompleteListbox,
  styleOverridesButton,
  styleOverridesCard,
  styleOverridesCheckbox,
  styleOverridesChip,
  styleOverridesDialogActions,
  styleOverridesDrawer,
  styleOverridesFormControl,
  styleOverridesFormLabel,
  styleOverridesGroupButton,
  styleOverridesInput,
  styleOverridesLink,
  styleOverridesList,
  styleOverridesMenu,
  styleOverridesModalDialog,
  styleOverridesSelect,
  styleOverridesSelectIndicator,
  styleOverridesSelectListbox,
  styleOverridesSwitch,
  styleOverridesTab,
  styleOverridesTabPanel,
  styleOverridesTabs,
  styleOverridesTooltip,
  styleOverridesTypography,
} from './styleOverrides';

export const theme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        danger: {
          outlinedColor: customColors.red.main,
          plainColor: customColors.red.main,
          softColor: customColors.red.main,
          solidBg: customColors.red.main,
        },
        neutral: {
          softBg: customColors.neutral.background,
          softOutlined: customColors.neutral.outline,
        },
        primary: {
          outlinedColor: customColors.blue.main,
          plainColor: customColors.blue.main,
          softColor: customColors.blue.main,
          solidBg: customColors.blue.main,
        },
        success: {
          solidBg: customColors.green.main,
        },
        warning: {
          softBg: customColors.yellow.main,
          solidBg: customColors.yellow.main,
        },
        ag: {
          input: {
            borderColor: customColors.ag.input.borderColor,
            disabledBackgroundColor: customColors.ag.input.disabledBackgroundColor,
            disabledBorderColor: customColors.ag.input.disabledBorderColor,
          },
          header: {
            backgroundColor: customColors.ag.header.backgroundColor,
          },
        },
        custom: {
          background: {
            dark: customColors.background.dark,
          },
          amber: customColors.amber.main,
          cyan: customColors.cyan.main,
          orange: customColors.orange.main,
          purple: customColors.purple.main,
          velvet: customColors.velvet.main,
          cloud: customColors.cloud.main,
          green: customColors.green.main,
          brown: {
            light: customColors.brown.light,
            main: customColors.brown.main,
            dark: customColors.brown.dark,
          },
          teal: customColors.teal.main,
          warning: {
            main: customColors.warning.main,
            light: customColors.warning.light,
            dark: customColors.warning.dark,
          },
        },
      },
    },
    dark: {
      palette: {
        mode: 'dark',
        background: {
          body: customColors.background.dark,
          surface: customColors.background.darker,
          level1: 'var(--joy-palette-neutral-100)',
          level2: 'var(--joy-palette-neutral-200)',
          level3: 'var(--joy-palette-neutral-300)',
        },
        danger: {
          mainChannel: '228 116 116',
          outlinedActiveBg: 'var(--joy-palette-danger-200)',
          outlinedBorder: 'var(--joy-palette-danger-300)',
          outlinedColor: customColors.red.dark,
          outlinedDisabledBorder: 'var(--joy-palette-neutral-200)',
          outlinedDisabledColor: 'var(--joy-palette-neutral-400)',
          outlinedHoverBg: 'var(--joy-palette-danger-100)',
          plainActiveBg: 'var(--joy-palette-danger-200)',
          plainColor: customColors.red.dark,
          plainDisabledColor: 'var(--joy-palette-neutral-400)',
          plainHoverBg: 'var(--joy-palette-danger-100)',
          softActiveBg: 'var(--joy-palette-danger-300)',
          softActiveColor: 'var(--joy-palette-danger-800)',
          softBg: 'var(--joy-palette-danger-100)',
          softColor: customColors.red.dark,
          softDisabledBg: 'var(--joy-palette-neutral-50)',
          softDisabledColor: 'var(--joy-palette-neutral-400)',
          softHoverBg: 'var(--joy-palette-danger-200)',
          solidActiveBg: 'var(--joy-palette-danger-700)',
          solidBg: customColors.red.dark,
          solidColor: 'var(--joy-palette-common-white)',
          solidDisabledBg: 'var(--joy-palette-neutral-100)',
          solidDisabledColor: 'var(--joy-palette-neutral-400)',
          solidHoverBg: 'var(--joy-palette-danger-600)',
          50: '#430A0A', // danger.800
          100: '#240505', // danger.900
          200: '#430A0A', // danger.800
          300: '#7D1212', // danger.700
          400: '#A51818', // danger.600
          500: '#C41C1C', // danger.500
          600: '#E47474', // danger.400
          700: '#F09898', // danger.300
          800: '#F7C5C5', // danger.200
          900: '#FCE4E4', // danger.100
        },
        neutral: {
          mainChannel: '159 166 173',
          outlinedActiveBg: 'var(--joy-palette-neutral-200)',
          outlinedBorder: 'var(--joy-palette-neutral-300)',
          outlinedColor: 'var(--joy-palette-neutral-700)',
          outlinedDisabledBorder: 'var(--joy-palette-neutral-200)',
          outlinedDisabledColor: 'var(--joy-palette-neutral-400)',
          outlinedHoverBg: 'var(--joy-palette-neutral-100)',
          plainActiveBg: 'var(--joy-palette-neutral-200)',
          plainColor: 'var(--joy-palette-neutral-700)',
          plainDisabledColor: 'var(--joy-palette-neutral-400)',
          plainHoverBg: 'var(--joy-palette-neutral-100)',
          plainHoverColor: 'var(--joy-palette-neutral-900)',
          softActiveBg: 'var(--joy-palette-neutral-300)',
          softActiveColor: 'var(--joy-palette-neutral-800)',
          softBg: customColors.neutral.darkBackground,
          softColor: 'var(--joy-palette-neutral-700)',
          softDisabledBg: 'var(--joy-palette-neutral-50)',
          softDisabledColor: 'var(--joy-palette-neutral-400)',
          softHoverBg: 'var(--joy-palette-neutral-200)',
          softOutlined: customColors.neutral.darkOutline,
          solidActiveBg: 'var(--joy-palette-neutral-700)',
          solidBg: 'var(--joy-palette-neutral-500)',
          solidColor: 'var(--joy-palette-common-black)',
          solidDisabledBg: 'var(--joy-palette-neutral-100)',
          solidDisabledColor: 'var(--joy-palette-neutral-400)',
          solidHoverBg: 'var(--joy-palette-neutral-600)',
          50: '#171A1C', // neutral.800
          100: '#0B0D0E', // neutral.900
          200: '#171A1C', // neutral.800
          300: '#32383E', // neutral.700
          400: '#555E68', // neutral.600
          500: '#636B74', // neutral.500
          600: '#9FA6AD', // neutral.400
          700: '#CDD7E1', // neutral.300
          800: '#DDE7EE', // neutral.200
          900: '#F0F4F8', // neutral.100
        },
        primary: {
          mainChannel: '67 147 228',
          outlinedActiveBg: 'var(--joy-palette-primary-200)',
          outlinedBorder: 'var(--joy-palette-primary-300)',
          outlinedColor: customColors.blue.dark,
          outlinedDisabledBorder: 'var(--joy-palette-neutral-200)',
          outlinedDisabledColor: 'var(--joy-palette-neutral-400)',
          outlinedHoverBg: 'var(--joy-palette-primary-100)',
          plainActiveBg: 'var(--joy-palette-primary-200)',
          plainColor: customColors.blue.dark,
          plainDisabledColor: 'var(--joy-palette-neutral-400)',
          plainHoverBg: 'var(--joy-palette-primary-200)',
          softActiveBg: 'var(--joy-palette-primary-300)',
          softActiveColor: 'var(--joy-palette-primary-800)',
          softBg: 'var(--joy-palette-primary-100)',
          softColor: customColors.blue.dark,
          softDisabledBg: 'var(--joy-palette-neutral-50)',
          softDisabledColor: 'var(--joy-palette-neutral-400)',
          softHoverBg: 'var(--joy-palette-primary-200)',
          solidActiveBg: 'var(--joy-palette-primary-700)',
          solidBg: customColors.blue.dark,
          solidColor: 'var(--joy-palette-common-white)',
          solidDisabledBg: 'var(--joy-palette-neutral-100)',
          solidDisabledColor: 'var(--joy-palette-neutral-400)',
          solidHoverBg: 'var(--joy-palette-primary-600)',
          50: '#0A2744', // primary.800
          100: '#051423', // primary.900
          200: '#0A2744', // primary.800
          300: '#12467B', // primary.700
          400: '#185EA5', // primary.600
          500: '#0B6BCB', // primary.500
          600: '#4393E4', // primary.400
          700: '#97C3F0', // primary.300
          800: '#C7DFF7', // primary.200
          900: '#E3EFFB', // primary.100
        },
        success: {
          mainChannel: '81 188 81',
          outlinedActiveBg: 'var(--joy-palette-success-200)',
          outlinedBorder: 'var(--joy-palette-success-300)',
          outlinedColor: 'var(--joy-palette-success-500)',
          outlinedDisabledBorder: 'var(--joy-palette-neutral-200)',
          outlinedDisabledColor: 'var(--joy-palette-neutral-400)',
          outlinedHoverBg: 'var(--joy-palette-success-100)',
          plainActiveBg: 'var(--joy-palette-success-200)',
          plainColor: 'var(--joy-palette-success-500)',
          plainDisabledColor: 'var(--joy-palette-neutral-400)',
          plainHoverBg: 'var(--joy-palette-success-100)',
          softActiveBg: 'var(--joy-palette-success-300)',
          softActiveColor: 'var(--joy-palette-success-800)',
          softBg: 'var(--joy-palette-success-100)',
          softColor: 'var(--joy-palette-success-700)',
          softDisabledBg: 'var(--joy-palette-neutral-50)',
          softDisabledColor: 'var(--joy-palette-neutral-400)',
          softHoverBg: 'var(--joy-palette-success-200)',
          solidActiveBg: 'var(--joy-palette-success-700)',
          solidBg: customColors.green.dark,
          solidColor: 'var(--joy-palette-common-white)',
          solidDisabledBg: 'var(--joy-palette-neutral-100)',
          solidDisabledColor: 'var(--joy-palette-neutral-400)',
          solidHoverBg: 'var(--joy-palette-success-600)',
          50: '#042F04', // success.800
          100: '#021D02', // success.900
          200: '#042F04', // success.800
          300: '#0A470A', // success.700
          400: '#136C13', // success.600
          500: '#1F7A1F', // success.500
          600: '#51BC51', // success.400
          700: '#A1E8A1', // success.300
          800: '#C7F7C7', // success.200
          900: '#E3FBE3', // success.100
        },
        warning: {
          mainChannel: '234 154 62',
          outlinedActiveBg: 'var(--joy-palette-warning-200)',
          outlinedBorder: 'var(--joy-palette-warning-300)',
          outlinedColor: 'var(--joy-palette-warning-500)',
          outlinedDisabledBorder: 'var(--joy-palette-neutral-200)',
          outlinedDisabledColor: 'var(--joy-palette-neutral-400)',
          outlinedHoverBg: 'var(--joy-palette-warning-100)',
          plainActiveBg: 'var(--joy-palette-warning-200)',
          plainColor: 'var(--joy-palette-warning-500)',
          plainDisabledColor: 'var(--joy-palette-neutral-400)',
          plainHoverBg: 'var(--joy-palette-warning-100)',
          softActiveBg: 'var(--joy-palette-warning-300)',
          softActiveColor: 'var(--joy-palette-warning-800)',
          softBg: 'var(--joy-palette-warning-400)',
          softColor: 'var(--joy-palette-warning-700)',
          softDisabledBg: 'var(--joy-palette-neutral-50)',
          softDisabledColor: 'var(--joy-palette-neutral-400)',
          softHoverBg: 'var(--joy-palette-warning-200)',
          solidActiveBg: 'var(--joy-palette-warning-700)',
          solidBg: 'var(--joy-palette-warning-500)',
          solidColor: 'var(--joy-palette-common-white)',
          solidDisabledBg: 'var(--joy-palette-neutral-100)',
          solidDisabledColor: 'var(--joy-palette-neutral-400)',
          solidHoverBg: 'var(--joy-palette-warning-600)',
          50: '#2E1B05', // warning.800
          100: '#1D1002', // warning.900
          200: '#2E1B05', // warning.800
          300: '#492B08', // warning.700
          400: '#72430D', // warning.600
          500: '#9A5B13', // warning.500
          600: '#EA9A3E', // warning.400
          700: '#F3C896', // warning.300
          800: '#FCE1C2', // warning.200
          900: '#FDF0E1', // warning.100
        },
        ag: {
          input: {
            borderColor: customColors.ag.input.darkBorderColor,
            disabledBackgroundColor: customColors.ag.input.darkDisabledBackgroundColor,
            disabledBorderColor: customColors.ag.input.darkDisabledBorderColor,
          },
          header: {
            backgroundColor: customColors.ag.header.darkBackgroundColor,
          },
        },
        custom: {
          background: {
            dark: customColors.background.darker,
          },
          amber: customColors.amber.dark,
          cyan: customColors.cyan.dark,
          orange: customColors.orange.dark,
          purple: customColors.purple.dark,
          velvet: customColors.velvet.main,
          cloud: customColors.cloud.main,
          green: customColors.green.main,
          brown: {
            light: customColors.brown.dark,
            main: customColors.brown.main,
            dark: customColors.brown.light,
          },
          teal: customColors.teal.main,
          warning: {
            light: customColors.warning.dark,
            main: customColors.warning.main,
            dark: customColors.warning.light,
          },
        },
        text: {
          primary: '#fff',
          secondary: '#aaa',
          tertiary: 'var(--joy-palette-neutral-600)',
        },
        common: {
          white: '#fff',
          black: '#181C20',
        },
      },
    },
  },
  components: {
    JoyButton: {
      defaultProps: {
        size: Size.SM,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => styleOverridesButton(ownerState, theme),
        startDecorator: {
          svg: {
            width: 18,
            height: 18,
          },
        },
        endDecorator: {
          svg: {
            width: 18,
            height: 18,
          },
        },
      },
    },
    JoyIconButton: {
      defaultProps: {
        size: Size.SM,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => styleOverridesButton(ownerState, theme),
      },
    },
    JoyMenuButton: {
      defaultProps: {
        size: Size.SM,
        endDecorator: <CaretDown weight="fill" />,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) =>
          styleOverridesButton(ownerState as ButtonOwnerState, theme),
      },
    },
    JoyButtonGroup: {
      defaultProps: {
        size: Size.SM,
        orientation: Orientation.HORIZONTAL,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => styleOverridesGroupButton(ownerState, theme),
      },
    },
    JoyInput: {
      defaultProps: {
        size: Size.SM,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => styleOverridesInput(ownerState, theme),
        input: {
          width: '100%',
        },
        startDecorator: {
          color: 'inherit',
        },
        endDecorator: {
          color: 'inherit',
        },
      },
    },
    JoyAutocomplete: {
      defaultProps: {
        color: Color.NEUTRAL,
        size: Size.SM,
        popupIcon: <CaretDown weight="fill" />,
      },
      styleOverrides: {
        root: ({ theme }) => styleOverridesAutocomplete(theme),
        listbox: ({ theme }) => styleOverridesAutocompleteListbox(theme),
      },
    },
    JoyMenu: {
      defaultProps: {
        size: Size.SM,
      },
      styleOverrides: {
        root: styleOverridesMenu(),
      },
    },
    JoyChip: {
      styleOverrides: {
        root: ({ ownerState, theme }) => styleOverridesChip(ownerState, theme),
      },
    },

    JoyDrawer: {
      defaultProps: {
        size: Size.MD,
      },
      styleOverrides: {
        root: ({ theme }) => styleOverridesDrawer(theme),
      },
    },
    JoyCheckbox: {
      defaultProps: {
        size: Size.SM,
      },
      styleOverrides: {
        root: styleOverridesCheckbox(),
      },
    },
    JoyTab: {
      styleOverrides: {
        root: ({ theme }) => styleOverridesTab(theme),
      },
    },
    JoyTabs: {
      styleOverrides: {
        root: styleOverridesTabs(),
      },
    },
    JoyTabPanel: {
      styleOverrides: {
        root: styleOverridesTabPanel(),
      },
    },
    JoyFormControl: {
      defaultProps: {
        size: Size.SM,
      },
      styleOverrides: {
        root: styleOverridesFormControl(),
      },
    },
    JoyFormLabel: {
      styleOverrides: {
        root: ({ theme }) => styleOverridesFormLabel(theme),
      },
    },
    JoySelect: {
      defaultProps: {
        size: Size.SM,
        indicator: <CaretDown weight="fill" />,
      },
      styleOverrides: {
        root: ({ theme }) => styleOverridesSelect(theme),
        listbox: ({ theme }) => styleOverridesSelectListbox(theme),
        indicator: styleOverridesSelectIndicator(),
      },
    },
    JoyTypography: {
      styleOverrides: {
        root: ({ theme }) => styleOverridesTypography(theme),
      },
    },
    JoyLink: {
      defaultProps: {
        level: Level.BODY_SM,
      },
      styleOverrides: {
        root: ({ theme }) => styleOverridesLink(theme),
      },
    },
    JoyCard: {
      defaultProps: {
        size: Size.SM,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => styleOverridesCard(ownerState, theme),
      },
    },
    JoyTextarea: {
      defaultProps: {
        size: Size.SM,
        minRows: 2,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => styleOverridesInput(ownerState, theme),
      },
    },
    JoyList: {
      defaultProps: {
        size: Size.SM,
      },
      styleOverrides: {
        root: ({ ownerState }) => styleOverridesList(ownerState),
      },
    },
    JoyTooltip: {
      defaultProps: {
        variant: Variant.SOFT,
        placement: TooltipPosition.BOTTOM_START,
        followCursor: true,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => styleOverridesTooltip(ownerState, theme),
      },
    },
    JoyStack: {
      defaultProps: {
        spacing: 0.5,
      },
    },
    JoyLinearProgress: {
      defaultProps: {
        size: Size.SM,
      },
    },
    JoyAlert: {
      styleOverrides: {
        root: ({ ownerState, theme }) => styleOverridesAlert(ownerState, theme),
      },
    },
    JoySwitch: {
      styleOverrides: {
        root: ({ ownerState, theme }) => styleOverridesSwitch(ownerState, theme),
      },
    },
    JoyModalDialog: {
      styleOverrides: {
        root: ({ ownerState, theme }) => styleOverridesModalDialog(ownerState, theme),
      },
    },
    JoyDialogActions: {
      styleOverrides: {
        root: () => styleOverridesDialogActions(),
      },
    },
  },
});
